/**
 * Contact — final section with direct channels and a message form.
 *
 * Lists direct contact channels (CHANNELS) and renders a validated form that
 * posts to the Web3Forms API. Validation runs on submit, per-field errors clear
 * on edit, and `status` drives the sending/success/error states.
 */
import { useRef, useState } from "react";
import "../ComponentsStyling/ContactSection.css";
import { useGlobalReveal } from "../utils/useGlobalReveal";
import { IconEmail, IconLinkedIn, IconGitHub } from "./ChannelIcons";

/* Direct contact channels rendered as a list of links.
   The glyphs come from ChannelIcons so the header's Links menu and this
   section cannot drift apart again. `icon` carries a per-channel size because
   the marks fill their 24-unit box very differently — LinkedIn is a solid
   square across all 24 units where GitHub is a circle across 20 — so drawing
   them at one shared size made LinkedIn read as much the heaviest of the
   three. These sizes give the three roughly equal optical weight. */
const CHANNELS = [
  {
    key: "email",
    label: "Email",
    handle: "khaledraedwork@gmail.com",
    href: "mailto:khaledraedwork@gmail.com",
    icon: <IconEmail size={23} />,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    handle: "Khaled Raed",
    href: "https://www.linkedin.com/in/khaled-raed-0a92412a7/",
    icon: <IconLinkedIn size={19} />,
  },
  {
    key: "github",
    label: "GitHub",
    handle: "KhaledRaed",
    href: "https://github.com/KhaledRaed",
    icon: <IconGitHub size={22} />,
  },
];

export default function Contact() {
  const [ref, inView] = useGlobalReveal({ threshold: 0.13, once: true });
  const [focused, setFocused] = useState(null);
  const [status, setStatus]   = useState(null);
  const [errors, setErrors]   = useState({});
  const formRef = useRef(null);

  // Collect per-field validation errors; name, email and message are required,
  // phone is optional but format-checked when present.
  function validate(form) {
    const errs = {};
    const name  = form.username.value.trim();
    const email = form.email.value.trim();
    const phone = form.phone.value.trim();
    const msg   = form.message.value.trim();

    if (!name)                          errs.name  = "Please enter your name.";
    if (!email)                         errs.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
                                        errs.email = "Please enter a valid email.";
    if (phone && !/^\+?[\d\s\-().]{6,20}$/.test(phone))
                                        errs.phone = "Please enter a valid phone number.";
    if (!msg)                           errs.msg   = "Please write a message.";

    return errs;
  }

  // Validate, then submit the form to Web3Forms; `status` reflects the outcome
  // and the form is reset on success.
  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(formRef.current);
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Move focus to the first invalid field so keyboard and screen-reader
      // users land on the problem instead of having to hunt for it.
      const fieldFor = { name: "username", email: "email", phone: "phone", msg: "message" };
      const firstBad = Object.keys(fieldFor).find(k => errs[k]);
      formRef.current.elements[fieldFor[firstBad]]?.focus();
      return;
    }
    setErrors({});
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: new FormData(formRef.current),
        headers: { Accept: "application/json" },
      });
      if (res.ok) { setStatus("ok"); formRef.current.reset(); setFocused(null); }
      else setStatus("err");
    } catch { setStatus("err"); }
  }

  // Clear a field's error as soon as the user edits it.
  function clearError(field) {
    if (errors[field]) setErrors(p => ({ ...p, [field]: null }));
  }

  return (
    <section className="ct" id="contact" ref={ref}>
      <div className="ct__dots"   aria-hidden="true" />
      <div className="ct__blob--a" aria-hidden="true" />
      <div className="ct__blob--b" aria-hidden="true" />
      {/* Section header: title and availability badge */}
      <div className={`ct__head ${inView ? "ct__head--in" : ""}`}>
        <div className="ct__eyebrow">
          <span className="ct__ey-num">06</span>
          <span className="ct__ey-line" aria-hidden="true" />
          <span className="ct__ey-label">Contact</span>
        </div>
        <div className="ct__headline-row">
          <h2 className="ct__h2">
            <span className="ct__h2-a" style={{ "--d": "0s" }}>Let's build</span>
            <span className="ct__h2-b" style={{ "--d": "0.07s" }}>
              something<em className="ct__h2-em"> great.</em>
            </span>
          </h2>
          <div className="ct__head-meta" style={{ "--d": "0.12s" }}>
            <span className="avail">Available for freelance</span>
            <span className="ct__head-loc">Jordan &amp; Remote</span>
          </div>
        </div>
      </div>
      <div className={`ct__sep ${inView ? "ct__sep--in" : ""}`} aria-hidden="true" />
      {/* Body: direct channels on the left, message form on the right */}
      <div className={`ct__body ${inView ? "ct__body--in" : ""}`}>
        {/* Direct contact channels */}
        <div className="ct__channels">
          <p className="ct__ch-label">Reach me directly</p>
          {/* mailto: opened in a new tab strands an empty tab once the mail
              client takes over, so only the http links go external. */}
          {CHANNELS.map((ch, i) => (
            <a
              key={ch.key}
              href={ch.href}
              {...(ch.href.startsWith("http")
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              className="ct__ch"
              style={{ "--i": i }}
            >
              {/* No external-link arrow. Pinned to the pane's right edge it
                  floated ~450px from its own text; moved inline it made a
                  ragged column, because the three names are different lengths
                  and there was nothing for the arrows to align to. It was also
                  wrong on the first row: a mailto: opens a mail client, it
                  does not navigate anywhere. The handle carries the
                  destination, and underlines itself on hover. */}
              <span className="ct__ch-icon">{ch.icon}</span>
              <span className="ct__ch-inner">
                <span className="ct__ch-platform">{ch.label}</span>
                <span className="ct__ch-handle">{ch.handle}</span>
              </span>
            </a>
          ))}
          <p className="ct__ch-footer">
            I reply within <strong>24 hours.</strong><br/>
            Let's make something together.
          </p>
        </div>
        {/* Message form */}
        <div className="ct__form-pane">
          <div className="ct__form-hd">
            <h3 className="ct__form-title">Send a message</h3>
            <p className="ct__form-sub">Tell me about your project or opportunity.</p>
          </div>
          <form ref={formRef} className="ct__form" onSubmit={handleSubmit} noValidate>
            {/* Web3Forms public access key (required by the endpoint) */}
            <input type="hidden" name="access_key" value="b86a3203-cc52-4dce-badb-748374fa8200" />
            <div className="ct__row">
              <div className={`ct__field${focused === "name" ? " ct__field--on" : ""}${errors.name ? " ct__field--err" : ""}`}>
                <label htmlFor="ct-name" className="ct__lbl">Name</label>
                <input id="ct-name" name="username" type="text" required autoComplete="name"
                  className="ct__inp" placeholder="Khaled Raed"
                  aria-invalid={errors.name ? true : undefined}
                  aria-describedby={errors.name ? "ct-name-err" : undefined}
                  onFocus={() => setFocused("name")}
                  onBlur={() => setFocused(null)}
                  onChange={() => clearError("name")} />
                {errors.name && <span className="ct__err" id="ct-name-err">{errors.name}</span>}
              </div>
              <div className={`ct__field${focused === "email" ? " ct__field--on" : ""}${errors.email ? " ct__field--err" : ""}`}>
                <label htmlFor="ct-email" className="ct__lbl">Email</label>
                <input id="ct-email" name="email" type="email" required autoComplete="email"
                  className="ct__inp" placeholder="you@example.com"
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={errors.email ? "ct-email-err" : undefined}
                  onFocus={() => setFocused("email")}
                  onBlur={() => setFocused(null)}
                  onChange={() => clearError("email")} />
                {errors.email && <span className="ct__err" id="ct-email-err">{errors.email}</span>}
              </div>
            </div>
            <div className={`ct__field${focused === "phone" ? " ct__field--on" : ""}${errors.phone ? " ct__field--err" : ""}`}>
              <label htmlFor="ct-phone" className="ct__lbl">Phone (optional)</label>
              <input id="ct-phone" name="phone" type="tel" autoComplete="tel"
                className="ct__inp" placeholder="Your phone number"
                aria-invalid={errors.phone ? true : undefined}
                aria-describedby={errors.phone ? "ct-phone-err" : undefined}
                onFocus={() => setFocused("phone")}
                onBlur={() => setFocused(null)}
                onChange={() => clearError("phone")} />
              {errors.phone && <span className="ct__err" id="ct-phone-err">{errors.phone}</span>}
            </div>
            <div className={`ct__field${focused === "msg" ? " ct__field--on" : ""}${errors.msg ? " ct__field--err" : ""}`}>
              <label htmlFor="ct-msg" className="ct__lbl">Message</label>
              <textarea id="ct-msg" name="message" required rows={4}
                className="ct__inp ct__inp--area" placeholder="Your message..."
                aria-invalid={errors.msg ? true : undefined}
                aria-describedby={errors.msg ? "ct-msg-err" : undefined}
                onFocus={() => setFocused("msg")}
                onBlur={() => setFocused(null)}
                onChange={() => clearError("msg")} />
              {errors.msg && <span className="ct__err" id="ct-msg-err">{errors.msg}</span>}
            </div>
            {/* Was `ct__form-actions`, which no stylesheet matched — the row
                never became a flex container, so the submit button and the
                status message stacked instead of sitting side by side. */}
            <div className="ct__foot">
              <button type="submit" className="btn btn--primary"
                disabled={status === "sending"} aria-busy={status === "sending"}>
                {status === "sending" ? (
                  <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <svg className="btn__icon btn__icon--arrow" viewBox="0 0 16 16"
                      fill="none" aria-hidden="true">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor"
                        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {status === "ok"  && <span className="ct__status ct__status--ok" role="status">Thank you! I'll reply soon.</span>}
              {status === "err" && <span className="ct__status ct__status--err" role="alert">Something went wrong. Try again.</span>}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}